import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box, Button, Typography, IconButton, Paper, CircularProgress,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { AudioRecorder as CkAudioRecorder } from '@NP-Webber/crm-kit';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';

/**
 * AudioRecorder — הקלטת שמע מהמיקרופון בדפדפן.
 *
 * Props:
 *   onSave(blob: Blob)  — נקרא כשהמשתמש לוחץ "שמור הקלטה"
 *   saving: boolean      — האם שמירה מתבצעת (מציג ספינר)
 *   disabled: boolean    — חסום (למשל בזמן העלאה)
 */
/** In the new design the crm-kit recorder (same props, new-design look) is used. */
const AudioRecorder = (props) => {
  const inside = useCkInside();
  return inside ? <CkAudioRecorder {...props} /> : <AudioRecorderLegacy {...props} />;
};

const AudioRecorderLegacy = ({ onSave, saving = false, disabled = false }) => {
  const [status, setStatus]         = useState('idle');      // idle | recording | recorded
  const [seconds, setSeconds]       = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [playing, setPlaying]       = useState(false);
  const [micError, setMicError]     = useState('');

  const mediaRecorder = useRef(null);
  const chunks         = useRef([]);
  const timerRef       = useRef(null);
  const audioRef       = useRef(null);
  const streamRef      = useRef(null);
  const blobRef        = useRef(null);

  // ניקוי בעת unmount
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      stopStream();
    };
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  /* ─── התחלת הקלטה ─── */
  const startRecording = useCallback(async () => {
    setMicError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // בחירת פורמט שמע נתמך (ללא MP4/video)
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
            ? 'audio/ogg;codecs=opus'
            : '';

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunks.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks.current, { type: recorder.mimeType });
        blobRef.current = blob;
        const url = URL.createObjectURL(blob);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(url);
        setStatus('recorded');
        stopStream();
      };

      recorder.start(250); // timeslice 250ms
      mediaRecorder.current = recorder;
      setSeconds(0);
      setStatus('recording');

      timerRef.current = setInterval(() => {
        setSeconds((s) => s + 1);
      }, 1000);
    } catch (err) {
      console.error('Mic access error:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setMicError('לא ניתנה הרשאת מיקרופון. יש לאשר גישה למיקרופון בדפדפן.');
      } else if (err.name === 'NotFoundError') {
        setMicError('לא נמצא מיקרופון במכשיר.');
      } else {
        setMicError('שגיאה בגישה למיקרופון.');
      }
    }
  }, [previewUrl]);

  /* ─── עצירת הקלטה ─── */
  const stopRecording = useCallback(() => {
    clearInterval(timerRef.current);
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
  }, []);

  /* ─── ניקוי הקלטה ─── */
  const discardRecording = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    blobRef.current = null;
    setPlaying(false);
    setSeconds(0);
    setStatus('idle');
  }, [previewUrl]);

  /* ─── play / pause ─── */
  const togglePlayback = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying(!playing);
  };

  const onAudioEnded = () => setPlaying(false);

  /* ─── שמירה ─── */
  const handleSave = () => {
    if (blobRef.current && onSave) {
      onSave(blobRef.current);
    }
  };

  /* ─── עיצוב זמן ─── */
  const formatTime = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mt: 1,
        borderStyle: 'dashed',
        bgcolor: status === 'recording' ? 'error.50' : 'grey.50',
        transition: 'background-color 0.3s',
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        🎙️ הקלטת שמע
      </Typography>

      {micError && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
          {micError}
        </Typography>
      )}

      {/* ─── מצב: idle ─── */}
      {status === 'idle' && (
        <Button
          variant="outlined"
          color="error"
          startIcon={<MicIcon />}
          onClick={startRecording}
          disabled={disabled || saving}
          size="small"
        >
          התחל הקלטה
        </Button>
      )}

      {/* ─── מצב: recording ─── */}
      {status === 'recording' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FiberManualRecordIcon
            color="error"
            sx={{
              fontSize: 16,
              animation: 'blink 1s infinite',
              '@keyframes blink': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.2 },
              },
            }}
          />
          <Typography variant="body2" fontWeight={600} color="error" sx={{ fontFamily: 'monospace' }}>
            {formatTime(seconds)}
          </Typography>
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<StopIcon />}
            onClick={stopRecording}
          >
            עצור הקלטה
          </Button>
        </Box>
      )}

      {/* ─── מצב: recorded (תצוגה מקדימה) ─── */}
      {status === 'recorded' && previewUrl && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <IconButton size="small" onClick={togglePlayback} color="primary">
              {playing ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {formatTime(seconds)}
            </Typography>
            <audio ref={audioRef} src={previewUrl} onEnded={onAudioEnded} />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || disabled}
            >
              {saving ? 'שומר...' : 'שמור הקלטה'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<MicIcon />}
              onClick={() => { discardRecording(); startRecording(); }}
              disabled={saving}
            >
              הקלט מחדש
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={discardRecording}
              disabled={saving}
            >
              מחק הקלטה
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default AudioRecorder;
